'use client';

import React, { useEffect } from 'react';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { getOrganizationAnalyticsProperties, useConfig } from '@/contexts/ConfigContext';
import { setOrganizationAnalyticsProperties } from '@/services/analytics/feature-tracking-service';

interface PostHogProviderProps {
  children: React.ReactNode;
}

let posthogInitialized = false;

export function PostHogProvider({ children }: PostHogProviderProps) {
  const config = useConfig();

  // Initialise PostHog once on the client
  useEffect(() => {
    if (posthogInitialized) return;

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'always',
    });
    // Start capturing session replays
    posthog.startSessionRecording();
    if (process.env.NODE_ENV === 'development') {
      // Log detailed PostHog debug output in the console
      posthog.debug();
    }
    posthogInitialized = true;
  }, []);

  // Adding analytic fields for all events, based on organization config (e.g. organization_id, organization_name)
  useEffect(() => {
    if (!config) {
      return;
    }

    const organizationProperties = getOrganizationAnalyticsProperties(
      window.location.hostname,
      config,
    );
    setOrganizationAnalyticsProperties(organizationProperties);

    posthog.register(organizationProperties);

    if (!organizationProperties.organization_id) {
      posthog.unregister('organization_id');
    }
  }, [config]);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
